import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  useSeo("Admin Sign In | Reform Intelligence®", "Administrator sign in for Reform Intelligence.");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex min-h-[70vh] items-center justify-center bg-[#F5F6F8] px-5 py-20">
      <div className="w-full max-w-md border border-[#E4E7EC] bg-white p-10">
        <div className="flex justify-center">
          <img src="/assets/emblem-navy.png" alt="Reform Intelligence" className="h-16 w-auto" />
        </div>
        <h1 className="mt-6 text-center font-serif2 text-2xl font-semibold text-[#1F2D4E]">Administrator sign in</h1>
        <p className="mt-2 text-center text-sm text-[#5A6B8C]">Reform Intelligence content management</p>
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-[#D4D8E0] bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#B08D3F]"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-[#D4D8E0] bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#B08D3F]"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-[#1F2D4E] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-[#98A0B3]">
          <Link to="/" className="underline underline-offset-2 hover:text-[#B08D3F]">Return to site</Link>
        </p>
      </div>
    </section>
  );
}
